/**
 * Chart Components for Audit Log Monitoring
 */

import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  Skeleton,
  Spinner,
  Center,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import {
  SuccessFailureRatio,
  TimeSeriesData,
  FailedUser,
  FailureReason,
  TopSource,
} from "./types";
import { formatDateTimeForDisplay } from "./utils";

interface ChartContainerProps {
  title: string;
  children: React.ReactNode;
  loading?: boolean;
}

const ChartContainer: React.FC<ChartContainerProps> = ({
  title,
  children,
  loading = false,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const titleColor = useColorModeValue("gray.900", "white");

  return (
    <Box
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      minH="400px"
    >
      <VStack align="stretch" spacing={4}>
        <Text fontSize="lg" fontWeight="bold" color={titleColor}>
          {title}
        </Text>
        {loading ? (
          <Center h="350px">
            <Spinner />
          </Center>
        ) : (
          children
        )}
      </VStack>
    </Box>
  );
};

interface SuccessFailureChartProps {
  data: SuccessFailureRatio | null;
  loading?: boolean;
}

export const SuccessFailureChart: React.FC<SuccessFailureChartProps> = ({
  data,
  loading = false,
}) => {
  if (!data) return null;

  const chartData = [
    { name: "Succes", value: data.success_count },
    { name: "Failed", value: data.failure_count },
  ];

  const COLORS = ["#4A5A33", "#E53E3E"];

  return (
    <ChartContainer title="Succes vs Failed" loading={loading}>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(entry) =>
              `${entry.name}: ${entry.value} (${(
                (entry.value / data.total) *
                100
              ).toFixed(2)}%)`
            }
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => `${value} login`}
            contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
          />
        </PieChart>
      </ResponsiveContainer>

      <VStack align="stretch" spacing={2} mt={4}>
        <HStack justify="space-between" fontSize="sm">
          <Text>Total Login:</Text>
          <Text fontWeight="bold">{data.total.toLocaleString("id-ID")}</Text>
        </HStack>
        <HStack justify="space-between" fontSize="sm">
          <Text>Success Rate:</Text>
          <Text fontWeight="bold" color="green.500">
            {data.success_percentage.toFixed(2)}%
          </Text>
        </HStack>
        <HStack justify="space-between" fontSize="sm">
          <Text>Failure Rate:</Text>
          <Text fontWeight="bold" color="red.500">
            {data.failure_percentage.toFixed(2)}%
          </Text>
        </HStack>
      </VStack>
    </ChartContainer>
  );
};

interface TimeSeriesChartProps {
  data: TimeSeriesData | null;
  loading?: boolean;
}

export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  data,
  loading = false,
}) => {
  if (!data || data.data.length === 0) return null;

  const chartData = data.data.map((point) => ({
    ...point,
    time: new Date(point.timestamp).toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    date: new Date(point.timestamp).toLocaleDateString("id-ID"),
  }));

  return (
    <ChartContainer title="Login Activity Over Time" loading={loading}>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey={data.interval === "hour" ? "time" : "date"}
            fontSize={12}
          />
          <YAxis />
          <Tooltip
            contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
            formatter={(value) => `${value} login`}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="count"
            stroke="#4A5A33"
            strokeWidth={2}
            dot={false}
            name="Total Login"
          />
          <Line
            type="monotone"
            dataKey="success_count"
            stroke="#2F855A"
            strokeWidth={2}
            dot={false}
            name="Succes"
          />
          <Line
            type="monotone"
            dataKey="failure_count"
            stroke="#E53E3E"
            strokeWidth={2}
            dot={false}
            name="Failed"
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface TopFailedUsersChartProps {
  data: FailedUser[] | null;
  loading?: boolean;
}

export const TopFailedUsersChart: React.FC<TopFailedUsersChartProps> = ({
  data,
  loading = false,
}) => {
  if (!data || data.length === 0) return null;

  const chartData = data.map((user) => ({
    ...user,
    shortEmail: user.email.length > 20 ? user.email.substring(0, 17) + "..." : user.email,
  }));

  return (
    <ChartContainer title="Top 10 Pengguna Gagal Login (Brute Force Detection)" loading={loading}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 200 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis dataKey="shortEmail" type="category" fontSize={12} />
          <Tooltip
            contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
            formatter={(value) => `${value} kali gagal`}
            labelFormatter={(label) => `Email: ${label}`}
          />
          <Bar dataKey="failure_count" fill="#E53E3E" radius={[0, 8, 8, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface TopFailureReasonsChartProps {
  data: FailureReason[] | null;
  loading?: boolean;
}

export const TopFailureReasonsChart: React.FC<TopFailureReasonsChartProps> = ({
  data,
  loading = false,
}) => {
  if (!data || data.length === 0) return null;

  return (
    <ChartContainer title="Top Alasan Kegagalan Login" loading={loading}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 300 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" />
          <YAxis
            dataKey="failure_reason"
            type="category"
            fontSize={12}
            width={290}
          />
          <Tooltip
            contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
            formatter={(value) => `${value} kali terjadi`}
          />
          <Bar dataKey="count" fill="#F6AD55" radius={[0, 8, 8, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface TopSourcesChartProps {
  data: TopSource[] | null;
  loading?: boolean;
}

export const TopSourcesChart: React.FC<TopSourcesChartProps> = ({
  data,
  loading = false,
}) => {
  if (!data || data.length === 0) return null;

  const chartData = data.map((source) => ({
    ...source,
    label: source.device_model ? `${source.source} (${source.device_model})` : source.source,
  }));

  const COLORS = ["#4A5A33", "#cbab70", "#2F855A", "#E53E3E", "#F6AD55"];

  return (
    <ChartContainer title="Top Sumber Login" loading={loading}>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={(entry) =>
              `${entry.label}: ${entry.count} (${(
                (entry.count / data.reduce((sum, d) => sum + d.count, 0)) *
                100
              ).toFixed(1)}%)`
            }
            outerRadius={80}
            fill="#8884d8"
            dataKey="count"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => `${value} login`}
            contentStyle={{ backgroundColor: "#fff", border: "1px solid #ccc" }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};
